/**
 * The test sheet's rules, apart from React (ADR-306): which picks a test sends,
 * and how a fact's value reads.
 */

import type {
  ComponentTestInput,
  ComponentTestInputKind,
  WorkflowTestInputs,
} from '@vagusx/clients';

export function has(inputs: readonly ComponentTestInput[], kind: ComponentTestInputKind): boolean {
  return inputs.some((i) => i.kind === kind);
}

export function spec(
  inputs: readonly ComponentTestInput[],
  kind: ComponentTestInputKind
): ComponentTestInput | undefined {
  return inputs.find((i) => i.kind === kind);
}

/**
 * The picks as the API takes them, or why the test cannot start yet. Pure and
 * exported so the rules are tested without rendering.
 */
export function testInputsBody(
  inputs: readonly ComponentTestInput[],
  picks: {
    providerId: string;
    locationId: string;
    appointmentTypeId: string;
    existing: { providerId: string; locationId: string; appointmentTypeId: string; at: string };
  }
): { body: WorkflowTestInputs } | { missing: string } {
  const body: WorkflowTestInputs = {};
  if (has(inputs, 'provider') && picks.providerId) body.providerId = picks.providerId;
  if (has(inputs, 'location') && picks.providerId && picks.locationId) {
    body.locationId = picks.locationId;
  }
  if (has(inputs, 'appointmentType') && picks.appointmentTypeId) {
    body.appointmentTypeId = picks.appointmentTypeId;
  }
  if (has(inputs, 'location') && picks.providerId && !picks.locationId) {
    return { missing: 'Pick the office for that provider.' };
  }

  const e = picks.existing;
  // Any pick starts it, so an incomplete appointment is flagged, never dropped.
  const started = Boolean(e.providerId || e.appointmentTypeId || e.at);
  if (has(inputs, 'existingAppointment') && started) {
    if (!e.providerId || !e.locationId || !e.at) {
      return { missing: 'Finish the existing appointment: provider, office and time.' };
    }
    body.existingAppointment = {
      providerId: e.providerId,
      locationId: e.locationId,
      ...(e.appointmentTypeId ? { appointmentTypeId: e.appointmentTypeId } : {}),
      startLocal: e.at,
    };
  }

  for (const input of inputs) {
    if (!input.required) continue;
    const given =
      input.kind === 'provider'
        ? body.providerId
        : input.kind === 'location'
          ? body.locationId
          : input.kind === 'appointmentType'
            ? body.appointmentTypeId
            : body.existingAppointment;
    if (!given) return { missing: `This test needs: ${input.label}.` };
  }
  return { body };
}

/** A fact's value, whatever shape the component wrote it in. */
export function formatFactValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return value.map((v) => formatFactValue(v)).join('; ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    // An offered or booked time says what it is in its own words.
    if (typeof record.displayTime === 'string') {
      return [record.displayTime, record.providerName, record.locationName]
        .filter((p): p is string => typeof p === 'string' && p.length > 0)
        .join(', ');
    }
    return Object.entries(record)
      .map(([k, v]) => `${k}: ${formatFactValue(v)}`)
      .join('; ');
  }
  return String(value);
}
